import { useEffect, useState } from "react";
import { ThemePreference, getThemePreference, setThemePreference, resolveTheme, watchSystemTheme } from "../lib/theme";

const OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: "system", label: "Match my device" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

/** The full three-way control, for the settings pages. */
export function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(getThemePreference);

  // Keeps "Match my device" honest while the page is open, rather than only as of page load.
  useEffect(() => watchSystemTheme(), []);

  function choose(next: ThemePreference) {
    setThemePreference(next);
    setPreference(next);
  }

  return (
    <div className="filter-chips" role="group" aria-label="Theme">
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`chip ${preference === option.value ? "active" : ""}`}
          aria-pressed={preference === option.value}
          onClick={() => choose(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path
        strokeLinecap="round"
        d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

/**
 * The compact flip, for the nav bar.
 *
 * Deliberately two-state where the settings control is three. Someone reaching for this in the chrome
 * wants the lights on or off right now; "match my device" is a preference you set once, and it lives on
 * the settings page with room to say what it means. Using this always sets an explicit choice, which is
 * the honest outcome -- you have just told the app what you want, so it stops guessing.
 */
export function ThemeQuickToggle() {
  const [current, setCurrent] = useState(() => resolveTheme(getThemePreference()));

  useEffect(() => watchSystemTheme(), []);

  function flip() {
    const next = current === "dark" ? "light" : "dark";
    setThemePreference(next);
    setCurrent(next);
  }

  return (
    <button
      type="button"
      className="chip nav-theme-toggle"
      onClick={flip}
      aria-label={current === "dark" ? "Switch to light theme" : "Switch to dark theme"}
      title={current === "dark" ? "Switch to light theme" : "Switch to dark theme"}
    >
      {current === "dark" ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
